
import { Link } from 'react-router';
import { cn } from '@/lib/utils';

export interface AppNavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** When true, open the link in a new tab. Used for Live Display so the
   *  management view stays open in the original tab. */
  openInNewTab?: boolean;
}

interface AppNavProps {
  items: AppNavItem[];
  pathname: string;
  className?: string;
  isCollapsed?: boolean;
}

/**
 * Shared sidebar navigation with unified active state by pathname.
 * Supports collapsed state showing only icons with tooltips.
 */
export function AppNav({ items, pathname, className, isCollapsed = false }: AppNavProps) {
  return (
    <nav className={cn('space-y-1', className)}>
      {items.map((item) => {
        const active = pathname === item.href;
        const linkClass = cn(
          'flex items-center rounded-lg text-sm font-medium transition-colors',
          isCollapsed ? 'justify-center px-3 py-3' : 'gap-3 px-4 py-3',
          active
            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
        );
        const content = (
          <>
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span>{item.name}</span>}
          </>
        );
        if (item.openInNewTab) {
          return (
            <a
              key={item.href + item.name}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              title={isCollapsed ? item.name : undefined}
              className={linkClass}
            >
              {content}
            </a>
          );
        }
        return (
          <Link
            key={item.href + item.name}
            to={item.href}
            title={isCollapsed ? item.name : undefined}
            className={linkClass}
          >
            {content}
          </Link>
        );
      })}
    </nav>
  );
}
